import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Box, Button, Stack } from '@mui/material';

import useNotification from '../../../../../../core/hooks/use_notification';
import ThreeLevelSteps from '../../../../common/steps';
import { createFoodPartnerOrderAction, createFoodPartnerOrderState } from '../../../../../../features/inventory/create_food_partner_order';
import { createAndSendFoodPartnerOrderAction, createAndSendFoodPartnerOrderState } from '../../../../../../features/inventory/create_and_send_food_partner_order';
import { createFoodPartnerOrderNotificationData } from '../../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../../core/enums/common/api';
import { ROUTE_FOOD_PARTNER_ORDERS } from '../../../../../../core/constants';
import SummaryList from '../../../../common/warehouse_and_food_partner_order_form/summary_list';
import _ from 'lodash';
import { InventoryServerErrorMessageEnum } from '../../../../../../core/enums/inventory/message';
import ConfirmDialog from '../../../../common/confirm_dialog';
import SectionPaper from "../../../../../common/section_paper";
import BottomActionBar from "../../../../../common/bottom_action_bar";

const Summary = ({ setView, data, setErrors }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { showNotification } = useNotification();

    const createFoodPartnerOrder = useSelector(createFoodPartnerOrderState);
    const createAndSendFoodPartnerOrder = useSelector(createAndSendFoodPartnerOrderState);

    const onReturn = () => {
        data?.form?.locationDivision
            ? setView(2)
            : setView(1);
    };

    const onSave = (generateLabels) => {
        const form = { ..._.omit(data?.form, ['attachments']), generateLabels };
        const attachments = data?.form?.attachments;

        onSubmit(createFoodPartnerOrderAction, { form, attachments });
    };

    const onSaveAndSend = (generateLabels) => {
        const form = { ..._.omit(data?.form, ['attachments']), generateLabels };
        const attachments = data?.form?.attachments;

        onSubmit(createAndSendFoodPartnerOrderAction, { form, attachments });
    };

    const onSubmit = (action, form) => {
        dispatch(action(form))
            .then(response => {
                showNotification(createFoodPartnerOrderNotificationData(response));

                if (response?.payload?.message?.substring(0, response?.payload?.message?.indexOf(' ')) === InventoryServerErrorMessageEnum.PRODUCT_PRICE_MISMATCH.errorCode) {
                    const errors = { productsList: [] };
                    const productId = response?.payload?.productId;

                    data?.formik?.destinationsList.forEach((_, index) => {
                        if (!errors.productsList[index]) {
                            errors.productsList[index] = [];
                        }

                        data?.formik?.productsList[index].forEach((product, productIndex) => {
                            if (product?.productId === productId) {
                                errors.productsList[index][productIndex] = {
                                    ...(errors.productsList[index][productIndex] || {}),
                                    price: 'Niejednolita cena'
                                };
                            }
                        });
                    });

                    setErrors(errors);
                    onReturn();
                }

                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && navigate(ROUTE_FOOD_PARTNER_ORDERS);
            });
    };

    const getSummaryList = () => {
        const locationDivision = data?.form?.locationDivision || (data?.formik?.destinationsList && data?.formik?.destinationsList.length > 0);
        const allProducts = data?.formik?.productsList.flat();
        const types = [...new Set(allProducts?.map(product => product?.type))];
        const productsByType = types?.map(type => [...allProducts?.filter(product => product?.type === type)]);
        const products = productsByType?.map((products, index) => {
            return [...new Set(productsByType[index]?.map(product => product?.name))]?.map(productName => {
                const productQuantity = products?.filter(product => product?.name === productName)
                    ?.map(product => product?.quantity)
                    ?.reduce((accumulator, current) => accumulator + current, 0);

                return {
                    name: productName,
                    quantity: productQuantity
                };
            });
        });
        const destinationProducts = types?.map(type => {
            return data?.formik?.destinationsList?.map((_, index) => {
                const productsByType = data?.formik?.productsList[index]?.filter(product => product?.type === type);

                return [...new Set(productsByType?.map(product => product?.name))]?.map(productName => {
                    const productQuantity = productsByType?.filter(product => product?.name === productName)
                        ?.map(product => product?.quantity)
                        ?.reduce((accumulator, current) => accumulator + current, 0);

                    return {
                        name: productName,
                        quantity: productQuantity
                    };
                });
            });
        });

        return (
            <SummaryList
                showDestinationProductsDialog={ locationDivision }
                types={ types }
                productsByType={ productsByType }
                products={ products }
                destinationsList={ data?.formik?.destinationsList }
                destinationProducts={ destinationProducts }
            />
        );
    };

    const isLoading = createFoodPartnerOrder?.loading || createAndSendFoodPartnerOrder?.loading;

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', gap: 3, flexGrow: 1 } }>
            <Box sx={ { flexGrow: 1, pb: 4 } }>
                <Stack spacing={ 3 }>
                    <ThreeLevelSteps thirdStep/>

                    <SectionPaper
                        title="Podsumowanie"
                        description={ data?.form?.locationDivision ? `Wybranych lokalizacji: ${ data?.formik?.destinationsList?.length || 0 }` : undefined }
                    >
                        <Box sx={ { mt: 2 } }>
                            { getSummaryList() }
                        </Box>
                    </SectionPaper>
                </Stack>
            </Box>

            <BottomActionBar>
                <Box sx={ { display: 'flex', justifyContent: 'space-between', width: '100%' } }>
                    <Button variant="outlined" onClick={ onReturn }>Wróć</Button>
                    <Stack direction="row" spacing={ 2 }>
                        <ConfirmDialog
                            isLoading={ isLoading }
                            title={ 'Wygenerować paczkę naklejek?' }
                            secondaryAction={ () => onSave(false) }
                            secondaryText={ 'Pomiń' }
                            primaryAction={ () => onSave(true) }
                            primaryText={ 'Wygeneruj' }
                            displayIcon={ false }
                            buttonText={ 'Zapisz zamówienie' }
                        />
                        <ConfirmDialog
                            isLoading={ isLoading }
                            title={ 'Wygenerować paczkę naklejek?' }
                            secondaryAction={ () => onSaveAndSend(false) }
                            secondaryText={ 'Pomiń' }
                            primaryAction={ () => onSaveAndSend(true) }
                            primaryText={ 'Wygeneruj' }
                            displayIcon={ false }
                            buttonText={ 'Zapisz i wyślij maila z zamówieniem' }
                        />
                    </Stack>
                </Box>
            </BottomActionBar>
        </Box>
    );
};

export default Summary;